import { useQueries, type QueryClient } from "@tanstack/react-query";
import {
  GetHeadToHeadPlayerGameLogQuery,
  GetHeadToHeadPlayerGameStatsQuery,
} from "@/graphql/gql/graphql";
import logger from "@/utils/logger";
import {
  getHeadToHeadPlayerGameLog,
  getHeadToHeadPlayerGameStats,
} from "../api";

// retries are handled once inside the api fetchers; don't multiply them here
const cacheOptions = { staleTime: Infinity, gcTime: Infinity, retry: false };
const getGameLogQueryKey = (eventId: number) => ["playerGameLog", eventId];
const getGameStatsQueryKey = (numberFirePlayerId: number) => [
  "playerGameStats",
  numberFirePlayerId,
];

export const prefetchGameLogAndStats = async (
  queryClient: QueryClient,
  eventId: number,
  numberFirePlayerId: number
) => {
  try {
    await Promise.allSettled([
      queryClient.prefetchQuery({
        queryKey: getGameLogQueryKey(eventId),
        queryFn: () => getHeadToHeadPlayerGameLog(eventId),
        ...cacheOptions,
      }),
      queryClient.prefetchQuery({
        queryKey: getGameStatsQueryKey(numberFirePlayerId),
        queryFn: () => getHeadToHeadPlayerGameStats(numberFirePlayerId),
        ...cacheOptions,
      }),
    ]);
  } catch (err) {
    logger.error("prefetchGameLogAndStats error", err);
  }
};

export const useGameLogAndStats = (
  eventId: number,
  numberFirePlayerId: number
) => {
  const [gameLogQuery, gameStatsQuery] = useQueries({
    queries: [
      {
        queryKey: getGameLogQueryKey(eventId),
        queryFn: () => getHeadToHeadPlayerGameLog(eventId),
        select: (response: GetHeadToHeadPlayerGameLogQuery) => response?.event,
        enabled: false,
        ...cacheOptions,
      },
      {
        queryKey: getGameStatsQueryKey(numberFirePlayerId),
        queryFn: () => getHeadToHeadPlayerGameStats(numberFirePlayerId),
        select: (response: GetHeadToHeadPlayerGameStatsQuery) =>
          response?.player,
        enabled: false,
        ...cacheOptions,
      },
    ],
  });

  return {
    gameLogData: gameLogQuery.data,
    gameStatsData: gameStatsQuery.data,
    isLoading: gameLogQuery.isLoading || gameStatsQuery.isLoading,
    isError: gameLogQuery.isError || gameStatsQuery.isError,
    gameLogRefetch: gameLogQuery.refetch,
    gameStatsRefetch: gameStatsQuery.refetch,
  };
};
